import axios, { AxiosResponse } from 'axios';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useRefresh } from '../auth/authenticationUtils';
import { logEvent } from 'firebase/analytics';
import { analytics } from '../firebase';
import { LOG_ERROR } from '../registro/Analytics/RegistroAnalytics';
import { operations } from '../types/api';
import { routesRegistryOpenApi } from '../types/routesRegistryOpenApi';
import { AUTH_ENTRYPOINT } from '../routing/api-routes';
import { toastOnError } from '../utils/toastUtils';
import useTranslatedException from '../api/exceptions/useTranslatedException';
import { Exception } from '../api/exceptions/Exception';
/* prettier-ignore */
import { AuthorizationAuthValue, authorizationContextValue } from '../auth/AuthorizationContext';
/* prettier-ignore */
import { ApiOpFn, ApiOpFnNullable, ApiPathParamsOf, ApiQueryParamsOf, ApiBodyOf, ApiOpFnResponseData } from '../types/openApiHelpers';
import { getLocalStorage } from '../localStorage/createPrefixedLocalStorage';

const noAuthConfig = () => {
    return { headers: { 'Content-Type': 'application/json' } };
};
const authConfig = () => {
    const prefixedStorage = getLocalStorage();
    return {
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${prefixedStorage.getItem('token')?.slice(1, -1)}`,
        },
    };
};

export const dynamicAuthConfig = (token: string | undefined | null) => {
    const prefixedStorage = getLocalStorage();
    if (token !== null && token !== undefined)
        return {
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
                'User-Key': prefixedStorage.getItem('user-key'),
            },
        };
    return { headers: { 'Content-Type': 'application/json' } };
};

export function usePost<ResponseData = any, RequestData = any>(
    authorization: AuthorizationAuthValue = authorizationContextValue,
) {
    const [loading, setLoading] = useState<boolean | undefined>();

    // @ts-ignore
    const refresh = useRefresh(authorization.tokenStorageName);
    const post = useCallback(
        async (
            url: string,
            data?: RequestData,
            getToken: () => string | null | undefined = () => {
                const prefixedStorage = getLocalStorage();
                return prefixedStorage
                    .getItem(authorization.tokenStorageName ?? '')
                    ?.slice(1, -1);
            },
        ) /* Promise<AxiosResponse<OperationResponse<T>>> */ => {
            setLoading(true);
            try {
                const response = await axios.post<ResponseData>(
                    url,
                    data,
                    authorization.auth
                        ? dynamicAuthConfig(getToken())
                        : noAuthConfig(),
                );
                setLoading(false);
                return response;
            } catch (reason) {
                if (reason.response.status === 401 && authorization.auth) {
                    await refresh();
                    const response = await axios.post<ResponseData>(
                        url,
                        data,
                        authorization.auth
                            ? dynamicAuthConfig(getToken())
                            : noAuthConfig(),
                    );
                    setLoading(false);
                    return response;
                } else {
                    setLoading(false);
                    logEvent(analytics, LOG_ERROR, {
                        error_code: reason,
                    });
                    throw reason;
                }
            }
        },
        [refresh, authorization.tokenStorageName, authorization.auth],
    );

    return useMemo(() => {
        return { post, loading };
    }, [post, loading]);
}

export function useGet<T = any>(
    authorization: AuthorizationAuthValue = authorizationContextValue,
) {
    const hasBeenCalledAtLeastOnce = useRef(false);
    const [loading, setLoading] = useState<boolean>(false);

    // @ts-ignore
    const refresh = useRefresh(authorization.tokenStorageName);
    // @ts-ignore
    const authSlice = useSelector((state) => state.auth);

    const get = useCallback(
        async (
            url: string,
            queryParams: object = {},
            getToken: () => string | null | undefined = () => {
                const prefixedStorage = getLocalStorage();
                return prefixedStorage
                    .getItem(authorization.tokenStorageName ?? '')
                    ?.slice(1, -1);
            },
        ): Promise<AxiosResponse<T>> => {
            setLoading(true);
            try {
                const response = await axios.get(
                    addQueryParams(url, queryParams),
                    authorization.auth
                        ? dynamicAuthConfig(getToken())
                        : noAuthConfig(),
                );
                setLoading(false);
                hasBeenCalledAtLeastOnce.current = true;
                return response;
            } catch (reason) {
                if (reason.response.status === 401 && authorization.auth) {
                    await refresh();
                    const response = await axios.get(
                        addQueryParams(url, queryParams),
                        authorization.auth
                            ? dynamicAuthConfig(getToken())
                            : noAuthConfig(),
                    );
                    setLoading(false);
                    hasBeenCalledAtLeastOnce.current = true;
                    return response;
                } else {
                    setLoading(false);
                    hasBeenCalledAtLeastOnce.current = true;
                    logEvent(analytics, LOG_ERROR, {
                        error_code: reason,
                    });
                    throw reason;
                }
            }
        },
        [authSlice],
    );
    return { get, loading, hasBeenCalledAtLeastOnce };
}

export function usePut(auth = true) {
    const [loading, setLoading] = useState<boolean>(false);

    const put = async (url: string, data: any) => {
        setLoading(true);
        return await axios
            .put(url, data, auth ? authConfig() : noAuthConfig())
            .then((response) => {
                setLoading(false);
                return response;
            })
            .catch((reason) => {
                setLoading(false);
                logEvent(analytics, LOG_ERROR, {
                    error_code: reason,
                });
                throw reason;
            });
    };

    return { put, loading };
}

export function useDelete(auth = true) {
    const [loading, setLoading] = useState<boolean>(false);

    const del = useCallback(
        async (url: string, config: object = {}) => {
            setLoading(true);
            return await axios
                .delete(url, {
                    ...(auth ? authConfig() : noAuthConfig()),
                    ...config,
                })
                .then((response) => {
                    setLoading(false);
                    return response;
                })
                .catch((reason) => {
                    setLoading(false);
                    throw reason;
                });
        },
        [auth],
    );

    return useMemo(() => {
        return { del, loading };
    }, [del, loading]);
}

// ======================================================D
// ======================================================D
// ======================================================D
// ======================================================D

/**
 * Opzioni di configurazione per lo useApi.
 *
 * Permette di personalizzare:
 * - il contesto di autorizzazione (token, login, ecc.)
 * - la gestione dei toast in caso di errore
 * - il comportamento in caso di errore (lanciare o restituire null)
 */
export interface UseApiOptions {
    /**
     * Configurazione di autorizzazione usata per leggere il token
     * e capire se la chiamata richiede autenticazione.
     */
    authorization: AuthorizationAuthValue;

    /**
     * Dove mostra automaticamente i toast di errore
     * usando le informazioni restituite dal backend.
     */
    onCatchShowOn: null | 'toast' | 'form' | 'both';

    /**
     * Se true, in caso di errore la funzione `call` NON lancia eccezioni,
     * ma restituisce `null`. Se false, continua a lanciare l’errore.
     */
    preventThrowError: boolean;
}

/**
 * Hook per invocare in modo type-safe di call su una operazione tipizzata nel backend tramite OpenAPI.
 *
 * - Risolve automaticamente URL, pathParams e queryParams a partire da `routesRegistryOpenApi`.
 * - Gestisce il token di autorizzazione e l’header Authorization.
 * - Converte automaticamente il body in FormData per richieste multipart/form-data.
 *
 * In base a `preventThrowError`, può lanciare gli errori oppure restituire `null`.
 * @param operation
 * @param options
 */
export function useApi<T extends keyof operations>(
    operation: T,
    options: Partial<UseApiOptions> & { preventThrowError: true },
): {
    call: ApiOpFnNullable<T>;
    loading: boolean | undefined;
    stopCall: () => void;
    calledOnce: boolean;
};
/**
 * Hook per invocare in modo type-safe di call su una operazione tipizzata nel backend tramite OpenAPI.
 *
 * - Risolve automaticamente URL, pathParams e queryParams a partire da `routesRegistryOpenApi`.
 * - Gestisce il token di autorizzazione e l’header Authorization.
 * - Converte automaticamente il body in FormData per richieste multipart/form-data.
 *
 * In base a `preventThrowError`, può lanciare gli errori oppure restituire `null`.
 * @param operation
 * @param options
 */
export function useApi<T extends keyof operations>(
    operation: T,
    options?: Partial<UseApiOptions> & { preventThrowError?: false },
): {
    call: ApiOpFn<T>;
    loading: boolean | undefined;
    stopCall: () => void;
    calledOnce: boolean;
};
export function useApi<T extends keyof operations>(
    operation: T,
    options?: Partial<UseApiOptions>,
) {
    const { authorization, onCatchShowOn, preventThrowError }: UseApiOptions = {
        authorization: authorizationContextValue,
        onCatchShowOn: 'toast',
        preventThrowError: false,
        ...options,
    };

    const { getTranslatedViolations, getTranslatedFormErrors } =
        useTranslatedException();
    const [loading, setLoading] = useState<boolean | undefined>();
    const controllerRef = useRef<AbortController | null>(null);
    const calledOnceRef = useRef<boolean>(false);
    // @ts-ignore
    const refresh = useRefresh(authorization.tokenStorageName);

    const tryGetExceptionError = (reason: any): any => {
        setLoading(false);
        try {
            const exception = Exception.fromResponse(reason.response);
            if (exception) {
                if (exception.content === undefined) {
                    if (onCatchShowOn)
                        toastOnError(`Error: ${exception.status}`);
                } else {
                    if (onCatchShowOn === 'toast') {
                        getTranslatedViolations(exception);
                        return exception;
                    }
                    if (onCatchShowOn === 'form' || onCatchShowOn === 'both') {
                        if (onCatchShowOn === 'both')
                            getTranslatedViolations(exception);
                        const newReasone = {
                            ...reason,
                            response: {
                                ...reason.response,
                                data: getTranslatedFormErrors(exception),
                            },
                        };
                        return newReasone;
                    }
                }
            }
        } catch (error) {
            console.error(error);
        }
        if (reason?.message && onCatchShowOn)
            toastOnError(`Error: ${reason?.message}`);
        return reason;
    };

    const call = useCallback(
        async ({
            pathParams,
            queryParams,
            body,
        }: {
            pathParams?: ApiPathParamsOf<T>;
            queryParams?: ApiQueryParamsOf<T>;
            body?: ApiBodyOf<T>;
        } = {}): Promise<AxiosResponse<ApiOpFnResponseData<T>> | null> => {
            const controller = new AbortController();
            controllerRef.current = controller;
            setLoading(true);
            const {
                apiMethod,
                pathParams: OApathParams,
                queryParams: OAqueryParams,
                path,
                contentType,
                responseType,
            } = routesRegistryOpenApi[operation];
            const baseUrl =
                AUTH_ENTRYPOINT +
                (OApathParams !== null && pathParams
                    ? fillPathTemplate<T>(path, pathParams)
                    : path);
            const url =
                OAqueryParams !== null &&
                typeof queryParams === 'object' &&
                Object.keys(queryParams as Record<string, unknown>).length > 0
                    ? addQueryParams(baseUrl, queryParams as any)
                    : baseUrl;

            let requestData: any = body;
            if (body && typeof body === 'object') {
                if (contentType === 'multipart/form-data') {
                    requestData = createFormDataFromObject(
                        body as Record<string, any>,
                    );
                    // logFormData(requestData); // * non eliminare ma commentare utile per debug
                }
            }

            const callConfig = () => {
                const prefixedStorage = getLocalStorage();
                const token = prefixedStorage
                    .getItem(authorization.tokenStorageName ?? '')
                    ?.slice(1, -1);
                const config: any = {
                    headers:
                        requestData instanceof FormData
                            ? {}
                            : { 'Content-Type': contentType },
                    responseType,
                    signal: controller.signal,
                };
                if (authorization.auth && token) {
                    config.headers.Authorization = `Bearer ${token}`;
                    config.headers['User-Key'] =
                        prefixedStorage.getItem('user-key');
                }
                return config;
            };

            const axiosCall = async (config: any) => {
                switch (apiMethod) {
                    case 'post':
                    case 'patch':
                    case 'put': {
                        return axios[apiMethod](url, requestData, config);
                    }

                    case 'delete':
                    case 'get': {
                        return axios[apiMethod](url, config);
                    }
                }
            };
            try {
                const response = await axiosCall(callConfig());
                setLoading(false);
                calledOnceRef.current = true;
                return response;
            } catch (reason: any) {
                if (axios.isCancel(reason)) {
                    setLoading(false);
                    throw new Error('Request aborted');
                }

                if (reason.response?.status === 401 && authorization.auth) {
                    try{
                        await refresh();
                    }catch(error){
                        setLoading(false)
                        calledOnceRef.current = true;
                        throw reason;
                    }

                    const response = await axiosCall(callConfig()).catch(
                        (reason: any) => {
                            const error = tryGetExceptionError(reason);
                            if (preventThrowError) {
                                calledOnceRef.current = true;
                                return null;
                            }
                            throw error;
                        },
                    );
                    calledOnceRef.current = true;
                    return response;
                } else {
                    logEvent(analytics, LOG_ERROR, {
                        error_code: reason,
                    });
                    const error = tryGetExceptionError(reason);
                    if (preventThrowError) {
                        calledOnceRef.current = true;
                        return null;
                    }
                    throw error;
                }
            }
        },
        [
            operation,
            refresh,
            authorization.tokenStorageName,
            authorization.auth,
        ],
    ) as ApiOpFnNullable<T>;

    const stopCall = useCallback(() => {
        controllerRef.current?.abort();
        controllerRef.current = null;
        setLoading(false);
    }, []);

    return useMemo(
        () => ({
            call,
            loading,
            stopCall,
            calledOnce: calledOnceRef.current,
        }),
        [call, loading, stopCall, calledOnceRef.current],
    );
}

const logFormData = (fd: FormData) => {
    const rows: Array<Record<string, any>> = [];
    fd.forEach((val, key) => {
        if (val instanceof File) {
            rows.push({
                key,
                filename: val.name,
                type: val.type,
                size: val.size,
            });
        } else {
            rows.push({ key, value: String(val) });
        }
    });
    console.groupCollapsed('FormData preview');
    console.table(rows);
    console.groupEnd();
};

function fillPathTemplate<T extends keyof operations>(
    path: string,
    pathParams?: ApiPathParamsOf<T>,
): string {
    if (!pathParams) return path;

    let result = path;
    for (const [key, value] of Object.entries(
        pathParams as Record<string, unknown>,
    )) {
        if (value === undefined || value === null) continue;
        const encoded = encodeURIComponent(String(value));
        const regex = new RegExp(`{${key}}`, 'g');
        result = result.replace(regex, encoded);
    }
    return result;
}

function createFormDataFromObject(data: Record<string, any>): FormData {
    const fd = new FormData();

    Object.entries(data).forEach(([key, value]) => {
        if (value === null || value === undefined) return;
        if (value instanceof File || value instanceof Blob) {
            fd.append(key, value);
            return;
        }
        if (Array.isArray(value)) {
            value.forEach((item, index) => {
                if (item instanceof File || item instanceof Blob) {
                    fd.append(`${key}[${index}]`, item);
                } else {
                    fd.append(`${key}[${index}]`, String(item));
                }
            });
            return;
        }
        fd.append(key, String(value));
    });

    return fd;
}

function addQueryParams<T extends object>(
    url: string,
    queryParamsObject: T | undefined,
) {
    if (queryParamsObject === undefined) return url;
    if (Object.entries(queryParamsObject).length === 0) return url;

    const params = new URLSearchParams();

    Object.entries(queryParamsObject).forEach(([key, value]) => {
        if (value === null || value === undefined || value === '') return;

        if (Array.isArray(value)) {
            value.forEach((item) => {
                if (item !== null && item !== undefined && item !== '') {
                    params.append(key, String(item));
                }
            });
            return;
        }

        params.append(key, String(value));
    });

    const queryString = params.toString();
    return queryString ? `${url}?${queryString}` : url;
}
